
import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2';
import { NavLink, useLocation } from 'react-router-dom';
import { Ico_Warning, useSnackbar } from '@sagecentral/core-ui';
import { ITaskOverview } from 'models/tasks';
import { useTaskComplete, useTaskContext, useTaskPermissions, useTaskStatusOptions } from 'hooks/tasks';
import { Helmet } from 'react-helmet';
import { toPascalCase } from 'utils/string-utils';

interface ILinkTabProps {
    value: number;
    text?: string;
    href?: string;
    selected?: boolean;
    hasAlert?: boolean;
};

const TaskTabsStyle = styled(Tabs)({
    color: '#333333',
    '& .MuiTabs-indicator': {
        color: '#001A69',
        backgroundColor: '#001A69',
        height: '3px',
        borderRadius: '4px',
    }
});

const TaskTabLinkStyle = styled(NavLink)(({ theme }) => ({
    paddingBottom: '10px',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '22px',
    color: '#333333',
    textTransform: 'uppercase',
    textDecoration: 'none',
    paddingLeft: '20px',
    paddingRight: '20px',

    '&.active': {
        color: theme.palette.secondary.main,
        fontWeight: 600
    }
}));

const TabLink = (props: ILinkTabProps) => {
    const theme = useTheme();
    return (
        <TaskTabLinkStyle
            tabIndex={0}
            to={props.href || ''}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                { props.text }
                { props.hasAlert ? <Ico_Warning htmlColor={theme.palette.error.main} sx={{ height: '20px', width: '20px' }} /> : null }
            </Box>
        </TaskTabLinkStyle>
    );
}

const LinkTab = (props: ILinkTabProps) => (
    <Tab 
        component={TabLink}
        {...props}
    />
);

const tabs: { text: string, path: string, hasAlert?: (task: ITaskOverview) => boolean }[] = [
    {
        text: 'TASK OVERVIEW',
        path: '/overview',
        hasAlert: (task: ITaskOverview) => (task.isProblem !== null ? task.isProblem : false)
    },
    {
        text: 'METADATA',
        path: '/metadata',
        hasAlert: (task: ITaskOverview) => (task.requirements?.some(req => req.tabKey === 'metadata' && !req.isDone) ?? false)
    },
    {
        text: 'ATTACHMENTS',
        path: '/attachments',
        hasAlert: (task: ITaskOverview) => (task.requirements?.some(req => req.tabKey === 'attachments' && req.isDone === false) ?? false)
    },
    {
        text: 'NOTES',
        path: '/notes'
    },
    {
        text: 'TASK DEPENDENCIES',
        path: '/dependencies'
    }
];

const TaskTabs = (props: { btnContainer: React.MutableRefObject<HTMLElement | null>}) => {

    const { btnContainer } = props;
    const taskContext = useTaskContext();
    const permissions = useTaskPermissions();
    const { displaySnackbar } = useSnackbar();
    const [ selectedTabName, setSelectedTabName ] = useState('');

    const { task, updateTaskOverview } = taskContext;

    const [ completeTask, working, CompletionDialog ] = useTaskComplete(
        async data => {
            updateTaskOverview(data)
            displaySnackbar('success', 'Task Completed Successfully');
        },
        error => displaySnackbar('warning', error)
    );
    const taskCanBeCompleted = taskContext.task && (taskContext.task?.requirements?.length === 0 || taskContext.task?.requirements?.every(r => r.isDone === true)) && !taskContext.task?.isProblem && !taskContext.task?.successorTaskMissingResource;
    const userCanComplete = permissions?.canChangeStatus;
    const currentURL: string = window.location.href;
    const suffix: string = currentURL.includes('all') ? 'all' : 'my';
    const location = useLocation();
    const { getOptionsForStatus, canCompleteFromStatus } = useTaskStatusOptions();

    //This also depends on the status transition possibilities
    //i.e. can we complete the task from the status we are
    const showCompleteButton = task && permissions?.canChangeStatus 
                                    && canCompleteFromStatus(task.statusId);


    useEffect(() => {
        for (let i = tabs.length - 1; i >= 0; i--) {
            if (location.pathname.includes(tabs[i].path)) {
                setSelectedTabName(tabs[i].text.toLowerCase().replace(' ', '-'));
                break;
            }
        }
    }, [location.pathname]);

    let selectedTab = 0;
    for (var i = tabs.length -1; i >= 0; i--) {
        if (location.pathname.includes(tabs[i].path)) {
            selectedTab = i;
            break;
        }
    }

    if (taskContext.task === null) return (<></>);

    return (
        <Box sx={{ width: '100%', mb: '34px', paddingTop: '20px' }}>
            <Helmet>
                <title>Sage Central | Task | {toPascalCase(selectedTabName)}</title>
            </Helmet>
            <Grid container sx={{ alignItems: 'flex-end' }}>
                <TaskTabsStyle value={selectedTab} aria-label="tasks tabs" TabIndicatorProps={{ sx: { display: 'block' } }}>
                {
                    tabs.map((tab, index) => (
                        <LinkTab 
                            value={index}
                            selected={index === selectedTab} 
                            text={tab.text} 
                            href={`/tasks/${taskContext.task?.id}${tab.path}`}
                            hasAlert={tab.hasAlert ? tab.hasAlert(taskContext.task!) : false}
                        />))
                }
                </TaskTabsStyle>
                <Grid xs={4} mdOffset='auto'>
                    <Box sx={{ float: 'right', display: 'flex', margin: '8px' }}>
                        <Box ref={btnContainer} />                        
                        { showCompleteButton
                            ? <Button variant='contained'
                                disabled={!taskCanBeCompleted || working || !userCanComplete} 
                                endIcon={working ? <CircularProgress size='1rem' /> : null}
                                onClick={() => completeTask()}
                            >
                                Complete Task
                            </Button>
                            : null }
                    </Box>
                </Grid>
            </Grid>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: '-2px', marginRight: '8px' }}></Box>
            { CompletionDialog }
        </Box>
    )
}

export default TaskTabs;